/**
 * People management for the Members, Invitations and Guests & agents settings: the searchable members table (members
 * plus pending email invites; `logic/roles#directoryRows`), role changes, deactivate / reactivate, primary-ownership
 * transfer, pending invites (revoke, resend) and the scope editor for guests and agents (the updateAccess route).
 *
 * Every action follows the permission matrix in `logic/roles` (the server enforces the same). Changes are optimistic:
 * the row changes at once and rolls back with a notice when the server refuses. Risky ones ask first (`confirm`):
 * deactivating, promoting to owner, dropping a guest's restriction, transferring primary ownership, revoking an
 * invite. Making someone a guest opens the scope editor first. Live: `member.updated` / `workspace.updated` events
 * update the table. Online only (offline writes are refused with a notice; the table stays readable).
 */
import type { AccessPolicy, Capabilities, DmPolicy, DirectoryPolicy, Member, PendingInvite, Role, Workspace } from "@bundle/contracts";
import { msg, type MessageDescriptor } from "../i18n.ts";
import { RESTRICTED_DEFAULTS, isRestricted } from "../logic/access.ts";
import {
  canManageInvite,
  canSeeAllInvites,
  deactivateProblem,
  directoryCounts,
  directoryRows,
  isManager,
  problemFromCode,
  problemMessage,
  reactivateProblem,
  roleChangeEffect,
  roleChangeProblem,
  roleChangedNotice,
  roleContext,
  transferProblem,
  type DirectoryFilter,
  type DirectoryRow,
  type RoleContext,
  type RoleProblem,
} from "../logic/roles.ts";
import { mergeMemberEvent } from "../logic/workspace.ts";
import { errorKind, type ApiFailure } from "../ports.ts";
import type { SettingsDeps } from "./SettingsSupport.ts";
import { ViewModel } from "./ViewModel.ts";
import type { WorkspaceViewModel } from "./WorkspaceViewModel.ts";

const DAY = 86_400_000;

/** A question before a risky action (the UI words it from `kind`, `name` and `role`). */
export type MemberConfirm =
  | { kind: "role"; userId: string; name: string; role: Role; clearsRestriction: boolean }
  | { kind: "deactivate"; userId: string; name: string }
  | { kind: "transfer"; userId: string; name: string }
  | { kind: "revoke"; inviteId: string; email?: string };

/** Boolean capabilities the scope editor toggles. */
export type ScopeCapability = "createChannels" | "invite" | "search" | "downloadFiles" | "joinHuddles" | "mentionBroadcast" | "seeEmails";
export const SCOPE_CAPABILITIES: ScopeCapability[] = ["search", "downloadFiles", "joinHuddles", "createChannels", "invite", "mentionBroadcast", "seeEmails"];
export const SCOPE_EXPIRY_DAYS: number[] = [0, 7, 30, 90];

export type ScopeEditor = {
  userId: string;
  name: string;
  /** "edit": a guest's or agent's access; "makeGuest": a member becomes a guest with this scope. */
  mode: "edit" | "makeGuest";
  isAgent: boolean;
  sponsorId?: string;
  sponsors: { userId: string; name: string }[];
  organization: string;
  /** Days from now (0 = never), or "keep" the current end. */
  expiry: number | "keep";
  currentExpiry?: number;
  can: Capabilities;
  busy: boolean;
  error?: MessageDescriptor;
};

export type InviteRow = {
  id: string;
  /** Absent: a shareable link. */
  email?: string;
  role: Role;
  expiresAt: number;
  useCount: number;
  createdByName: string;
  canManage: boolean;
};

export type MembersState = {
  status: "loading" | "ready" | "error";
  query: string;
  filter: DirectoryFilter;
  rows: DirectoryRow[];
  counts: Record<DirectoryFilter, number>;
  /** Guests & agents section (independent of the table's query and filter). */
  guests: DirectoryRow[];
  agents: DirectoryRow[];
  invites: InviteRow[];
  /** Owners/admins see every invite; others only their own. */
  seesAllInvites: boolean;
  /** I'm an owner or admin. */
  canManage: boolean;
  primaryOwnerId?: string;
  /** Row keys with a write in flight. */
  pending: string[];
  confirm?: MemberConfirm;
  scope?: ScopeEditor;
  /** A resent invite's new link (to copy). */
  resent?: { inviteId: string; url: string };
  notice?: MessageDescriptor;
};

const NO_COUNTS: Record<DirectoryFilter, number> = { all: 0, active: 0, deactivated: 0, invited: 0, admins: 0, guests: 0, agents: 0 };

export class MembersViewModel extends ViewModel<MembersState> {
  readonly ws: WorkspaceViewModel;
  private readonly deps: SettingsDeps;
  /** The directory from the server (emails included), updated by events and my writes. */
  private members = new Map<string, Member>();
  private pendingInvites: PendingInvite[] = [];
  /** Optimistic versions of members with a write in flight. */
  private readonly overrides = new Map<string, Member>();
  private workspace: Workspace | undefined;
  private lastCache: Record<string, Member> | undefined;
  private lastWorkspace: Workspace | undefined;

  constructor(ws: WorkspaceViewModel, deps: SettingsDeps) {
    super({ status: "loading", query: "", filter: "all", rows: [], counts: NO_COUNTS, guests: [], agents: [], invites: [], seesAllInvites: false, canManage: false, pending: [] });
    this.ws = ws;
    this.deps = deps;
    this.onDispose(ws.subscribe(() => this.follow()));
    this.follow();
  }

  private get wsId(): string {
    return this.ws.wsId;
  }

  private now(): number {
    return this.ws.deps.clock.now();
  }

  // ---------- Data ----------

  async load(): Promise<void> {
    const [members, invites, workspace] = await Promise.allSettled([this.deps.api.members(this.wsId), this.deps.api.invites(this.wsId), this.deps.api.workspace(this.wsId)]);
    if (this.isDisposed) return;
    if (members.status === "fulfilled") {
      this.members = new Map(members.value.map((m) => [m.userId, m]));
    } else if (this.members.size === 0) {
      // Offline: the cached members (no emails) until the directory loads.
      const cached = this.ws.state.cache?.members;
      if (cached === undefined) {
        this.update({ status: "error" });
        return;
      }
      this.members = new Map(Object.values(cached).map((m) => [m.userId, m]));
    }
    if (invites.status === "fulfilled") this.pendingInvites = invites.value;
    if (workspace.status === "fulfilled") this.workspace = workspace.value;
    this.update({ status: "ready" });
    this.derive();
  }

  /** Live changes: member.updated (merged, keeping emails) and workspace.updated. */
  private follow(): void {
    const cache = this.ws.state.cache;
    if (cache === undefined) return;
    let changed = false;
    if (this.lastCache !== cache.members) {
      for (const [id, m] of Object.entries(cache.members)) {
        if (this.lastCache !== undefined && this.lastCache[id] === m) continue;
        if (this.state.status === "loading" && this.members.size === 0) continue;
        this.members.set(id, mergeMemberEvent(this.members.get(id), m));
        changed = true;
      }
      this.lastCache = cache.members;
    }
    if (this.lastWorkspace !== cache.workspace) {
      if (this.lastWorkspace !== undefined || this.workspace === undefined) this.workspace = { ...this.workspace, ...cache.workspace };
      this.lastWorkspace = cache.workspace;
      changed = true;
    }
    if (changed && this.state.status === "ready") this.derive();
  }

  private list(): Member[] {
    const out: Member[] = [];
    for (const [id, m] of this.members) out.push(this.overrides.get(id) ?? m);
    return out;
  }

  private me(): Member | undefined {
    const id = this.ws.state.meId;
    return this.overrides.get(id) ?? this.members.get(id) ?? this.ws.state.cache?.members[id];
  }

  private ctx(): RoleContext {
    const ws = this.workspace ?? this.ws.state.cache?.workspace;
    const now = this.now();
    return ws === undefined ? { ownerCount: 0, now } : roleContext(ws, this.list(), now);
  }

  private derive(): void {
    const me = this.me();
    if (me === undefined) return;
    const members = this.list();
    const ctx = this.ctx();
    const s = this.state;
    const all = directoryRows(members, this.pendingInvites, me, ctx, "", "all");
    const names = new Map(members.map((m) => [m.userId, m.displayName]));
    const invites = this.pendingInvites
      .toSorted((a, b) => b.createdAt - a.createdAt)
      .map((i): InviteRow => {
        const r: InviteRow = { id: i.id, role: i.role, expiresAt: i.expiresAt, useCount: i.useCount, createdByName: names.get(i.createdBy) ?? i.createdBy, canManage: canManageInvite(me, i, ctx.now) };
        if (i.email !== undefined) r.email = i.email;
        return r;
      });
    const next: MembersState = {
      ...s,
      rows: directoryRows(members, this.pendingInvites, me, ctx, s.query, s.filter),
      counts: directoryCounts(all),
      guests: all.filter((r) => r.kind === "human" && r.restricted && r.status !== "invited"),
      agents: all.filter((r) => r.kind === "agent"),
      invites,
      seesAllInvites: canSeeAllInvites(me, ctx.now),
      canManage: isManager(me, ctx.now),
    };
    if (ctx.primaryOwnerId === undefined) delete next.primaryOwnerId;
    else next.primaryOwnerId = ctx.primaryOwnerId;
    this.setState(next);
  }

  setQuery(query: string): void {
    this.update({ query });
    this.derive();
  }

  setFilter(filter: DirectoryFilter): void {
    this.update({ filter });
    this.derive();
  }

  // ---------- Writes ----------

  private member(userId: string): Member | undefined {
    return this.overrides.get(userId) ?? this.members.get(userId);
  }

  private refuse(problem: RoleProblem): void {
    this.update({ notice: problemMessage(problem) });
  }

  private failure(e: unknown): MessageDescriptor {
    const code = (e as ApiFailure).code;
    const problem = typeof code === "string" ? problemFromCode(code) : undefined;
    if (problem !== undefined) return problemMessage(problem);
    return errorKind(e) === "network" ? msg("settings.offline") : msg("members.notice.failed");
  }

  private setPending(key: string, on: boolean): void {
    const has = this.state.pending.includes(key);
    if (on && !has) this.update({ pending: [...this.state.pending, key] });
    if (!on && has) this.update({ pending: this.state.pending.filter((k) => k !== key) });
  }

  /** Optimistic write on one member: shows `optimistic` until the server answers; rolls back on failure. */
  private async write(userId: string, optimistic: Member, call: () => Promise<Member>, done: (m: Member) => MessageDescriptor): Promise<boolean> {
    this.overrides.set(userId, optimistic);
    this.setPending(userId, true);
    this.derive();
    try {
      const m = await call();
      if (this.isDisposed) return true;
      this.members.set(userId, mergeMemberEvent(this.members.get(userId), m));
      this.update({ notice: done(m) });
      return true;
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: this.failure(e) });
      return false;
    } finally {
      if (!this.isDisposed) {
        this.overrides.delete(userId);
        this.setPending(userId, false);
        this.derive();
      }
    }
  }

  /** Role menu choice: applied now, or asked first, or the scope editor (→ guest). */
  requestRole(userId: string, role: Role): void {
    const me = this.me();
    const target = this.member(userId);
    if (me === undefined || target === undefined) return;
    const problem = roleChangeProblem(me, target, role, this.ctx());
    if (problem === "unchanged") return;
    if (problem !== undefined) return this.refuse(problem);
    const effect = roleChangeEffect(target, role);
    if (effect === "needsScope") return this.openScope(userId, "makeGuest");
    if (effect === "clearsRestriction" || role === "owner") {
      this.update({ confirm: { kind: "role", userId, name: target.displayName, role, clearsRestriction: effect === "clearsRestriction" } });
      return;
    }
    void this.applyRole(userId, role);
  }

  private applyRole(userId: string, role: Role, access?: AccessPolicy): Promise<boolean> {
    const target = this.member(userId);
    if (target === undefined) return Promise.resolve(false);
    const optimistic: Member = { ...target, role };
    if (access !== undefined) optimistic.access = access;
    else if (roleChangeEffect(target, role) === "clearsRestriction") delete optimistic.access;
    const req = access === undefined ? { role } : { role, access };
    return this.write(userId, optimistic, () => this.deps.api.changeRole(this.wsId, userId, req), (m) => roleChangedNotice(m.displayName, role));
  }

  requestDeactivate(userId: string): void {
    const me = this.me();
    const target = this.member(userId);
    if (me === undefined || target === undefined) return;
    const problem = deactivateProblem(me, target, this.ctx());
    if (problem !== undefined) return this.refuse(problem);
    this.update({ confirm: { kind: "deactivate", userId, name: target.displayName } });
  }

  reactivate(userId: string): void {
    const me = this.me();
    const target = this.member(userId);
    if (me === undefined || target === undefined) return;
    const problem = reactivateProblem(me, target, this.ctx());
    if (problem !== undefined) return this.refuse(problem);
    void this.write(userId, { ...target, deactivated: false }, () => this.deps.api.reactivate(this.wsId, userId), (m) => msg("members.notice.reactivated", { name: m.displayName }));
  }

  requestTransfer(userId: string): void {
    const me = this.me();
    const target = this.member(userId);
    if (me === undefined || target === undefined) return;
    const problem = transferProblem(me, target, this.ctx());
    if (problem !== undefined) return this.refuse(problem);
    this.update({ confirm: { kind: "transfer", userId, name: target.displayName } });
  }

  requestRevoke(inviteId: string): void {
    const inv = this.pendingInvites.find((i) => i.id === inviteId);
    const me = this.me();
    if (inv === undefined || me === undefined || !canManageInvite(me, inv, this.now())) return;
    const confirm: MemberConfirm = { kind: "revoke", inviteId };
    if (inv.email !== undefined) confirm.email = inv.email;
    this.update({ confirm });
  }

  cancelConfirm(): void {
    this.update({ confirm: undefined });
  }

  /** Runs the confirmed action. */
  async confirm(): Promise<void> {
    const c = this.state.confirm;
    if (c === undefined) return;
    this.update({ confirm: undefined });
    switch (c.kind) {
      case "role":
        await this.applyRole(c.userId, c.role);
        return;
      case "deactivate": {
        const target = this.member(c.userId);
        if (target === undefined) return;
        await this.write(c.userId, { ...target, deactivated: true }, () => this.deps.api.deactivate(this.wsId, c.userId), (m) => msg("members.notice.deactivated", { name: m.displayName }));
        return;
      }
      case "transfer": {
        this.setPending(c.userId, true);
        try {
          this.workspace = await this.deps.api.transferOwnership(this.wsId, c.userId);
          if (this.isDisposed) return;
          this.update({ notice: msg("members.notice.transferred", { name: c.name }) });
          await this.load();
        } catch (e) {
          if (!this.isDisposed) this.update({ notice: this.failure(e) });
        } finally {
          if (!this.isDisposed) {
            this.setPending(c.userId, false);
            this.derive();
          }
        }
        return;
      }
      case "revoke": {
        const before = this.pendingInvites;
        this.pendingInvites = before.filter((i) => i.id !== c.inviteId);
        this.derive();
        try {
          await this.deps.api.revokeInvite(this.wsId, c.inviteId);
          if (!this.isDisposed) this.update({ notice: msg("members.notice.revoked") });
        } catch (e) {
          if (this.isDisposed) return;
          this.pendingInvites = before;
          this.update({ notice: this.failure(e) });
          this.derive();
        }
        return;
      }
    }
  }

  /** A new token for an invite (the old one stops working); email invites are emailed again. */
  async resend(inviteId: string): Promise<void> {
    const key = `invite:${inviteId}`;
    this.setPending(key, true);
    try {
      const invite = await this.deps.api.resendInvite(this.wsId, inviteId);
      if (this.isDisposed) return;
      this.pendingInvites = await this.deps.api.invites(this.wsId).catch(() => this.pendingInvites.filter((i) => i.id !== inviteId));
      if (this.isDisposed) return;
      this.update({ resent: { inviteId: invite.id, url: invite.url }, notice: invite.email !== undefined && invite.emailed ? msg("members.notice.resent") : msg("members.notice.newLink") });
    } catch (e) {
      if (!this.isDisposed) this.update({ notice: this.failure(e) });
    } finally {
      if (!this.isDisposed) {
        this.setPending(key, false);
        this.derive();
      }
    }
  }

  /** Invite links/emails were created elsewhere (the invite sheet): refresh the pending list. */
  async refreshInvites(): Promise<void> {
    try {
      const invites = await this.deps.api.invites(this.wsId);
      if (this.isDisposed) return;
      this.pendingInvites = invites;
      this.derive();
    } catch {
      // keep what we have
    }
  }

  dismissResent(): void {
    this.update({ resent: undefined });
  }

  dismissNotice(): void {
    this.update({ notice: undefined });
  }

  // ---------- Scope editor (guests and agents) ----------

  openScope(userId: string, mode: "edit" | "makeGuest" = "edit"): void {
    const target = this.member(userId);
    const me = this.me();
    if (target === undefined || me === undefined) return;
    const policy = mode === "edit" ? target.access : undefined;
    const sponsors = this.list()
      .filter((m) => m.kind === "human" && !m.deactivated && !isRestricted(m) && m.role !== "guest" && m.userId !== userId)
      .map((m) => ({ userId: m.userId, name: m.displayName }))
      .toSorted((a, b) => (a.name.toLowerCase() < b.name.toLowerCase() ? -1 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : 0));
    const editor: ScopeEditor = {
      userId,
      name: target.displayName,
      mode,
      isAgent: target.kind === "agent",
      sponsors,
      organization: policy?.organization?.name ?? "",
      expiry: policy?.expiresAt === undefined ? (mode === "makeGuest" ? 30 : 0) : "keep",
      can: policy === undefined ? { ...RESTRICTED_DEFAULTS, joinHuddles: false } : { ...policy.can },
      busy: false,
    };
    const sponsorId = policy?.sponsorId ?? me.userId;
    editor.sponsorId = sponsorId;
    if (policy?.expiresAt !== undefined) editor.currentExpiry = policy.expiresAt;
    this.update({ scope: editor });
  }

  private editScope(patch: Partial<ScopeEditor>): void {
    const s = this.state.scope;
    if (s !== undefined) this.update({ scope: { ...s, ...patch, error: undefined } });
  }

  setScopeSponsor(userId: string): void {
    this.editScope({ sponsorId: userId });
  }

  setScopeOrganization(name: string): void {
    this.editScope({ organization: name });
  }

  setScopeExpiry(expiry: number | "keep"): void {
    this.editScope({ expiry });
  }

  setScopeCapability(key: ScopeCapability, on: boolean): void {
    const s = this.state.scope;
    if (s !== undefined) this.editScope({ can: { ...s.can, [key]: on } });
  }

  setScopeDm(dm: DmPolicy): void {
    const s = this.state.scope;
    if (s !== undefined) this.editScope({ can: { ...s.can, dm } });
  }

  setScopeDirectory(seeDirectory: DirectoryPolicy): void {
    const s = this.state.scope;
    if (s !== undefined) this.editScope({ can: { ...s.can, seeDirectory } });
  }

  closeScope(): void {
    if (this.state.scope?.busy !== true) this.update({ scope: undefined });
  }

  /** The access policy the editor describes (kept: page shares, channel cap, an org's domain). */
  scopePolicy(): AccessPolicy | undefined {
    const s = this.state.scope;
    const target = s === undefined ? undefined : this.member(s.userId);
    if (s === undefined || target === undefined) return undefined;
    const prev = s.mode === "edit" ? target.access : undefined;
    const policy: AccessPolicy = { level: prev?.level ?? "restricted", can: s.can };
    if (s.sponsorId !== undefined) policy.sponsorId = s.sponsorId;
    const org = s.organization.trim();
    if (org !== "") policy.organization = prev?.organization !== undefined && prev.organization.name === org ? prev.organization : { name: org };
    if (s.expiry === "keep") {
      if (s.currentExpiry !== undefined) policy.expiresAt = s.currentExpiry;
    } else if (s.expiry > 0) policy.expiresAt = this.now() + s.expiry * DAY;
    if (prev?.pageIds !== undefined) policy.pageIds = prev.pageIds;
    if (prev?.maxChannels !== undefined) policy.maxChannels = prev.maxChannels;
    return policy;
  }

  async saveScope(): Promise<void> {
    const s = this.state.scope;
    const policy = this.scopePolicy();
    const target = s === undefined ? undefined : this.member(s.userId);
    if (s === undefined || policy === undefined || target === undefined || s.busy) return;
    this.update({ scope: { ...s, busy: true } });
    const ok =
      s.mode === "makeGuest"
        ? await this.applyRole(s.userId, "guest", policy)
        : await this.write(s.userId, { ...target, access: policy }, () => this.deps.api.updateAccess(this.wsId, s.userId, policy), (m) => msg("members.notice.scopeSaved", { name: m.displayName }));
    if (this.isDisposed) return;
    const cur = this.state.scope;
    if (ok) this.update({ scope: undefined });
    else if (cur !== undefined) this.update({ scope: { ...cur, busy: false, error: this.state.notice ?? msg("members.notice.failed") } });
  }
}
